import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import {
  UserPlus, Users, Briefcase, ArrowLeft, Plus, Clock, CheckCircle, Search
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const RecruitmentPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    position_title: "",
    department: "",
    job_description: "",
    required_skills: "",
    experience_years: "",
    salary_range_min: "",
    salary_range_max: "",
    urgency: "normal"
  });
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/admin/recruitment`, { headers });
        setRequests(res.data.requests || []);
      } catch (error) {
        console.error("Error:", error);
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/recruitment`, {
        ...formData,
        required_skills: formData.required_skills.split(",").map(s => s.trim()),
        experience_years: parseInt(formData.experience_years),
        salary_range_min: parseFloat(formData.salary_range_min),
        salary_range_max: parseFloat(formData.salary_range_max)
      }, { headers });
      toast.success("Recruitment request created");
      setShowForm(false);
      const res = await axios.get(`${API}/admin/recruitment`, { headers });
      setRequests(res.data.requests || []);
    } catch (error) {
      toast.error("Failed to create recruitment request");
    }
  };

  const formatCurrency = (val) => {
    if (!val) return "$0";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(val);
  };

  const getUrgencyBadge = (urgency) => {
    const colors = {
      urgent: "bg-red-500/20 text-red-400",
      normal: "bg-blue-500/20 text-blue-400",
      low: "bg-slate-500/20 text-slate-400"
    };
    return colors[urgency] || "bg-slate-500/20 text-slate-400";
  };

  const getStatusBadge = (status) => {
    const colors = {
      open: "bg-green-500/20 text-green-400",
      interviewing: "bg-yellow-500/20 text-yellow-400",
      closed: "bg-slate-500/20 text-slate-400",
      filled: "bg-purple-500/20 text-purple-400"
    };
    return colors[status] || "bg-slate-500/20 text-slate-400";
  };

  const openPositions = requests.filter(r => r.status === "open" || r.status === "interviewing").length;
  const totalApplicants = requests.reduce((sum, r) => sum + (r.applicants || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading Recruitment...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1c2d] p-6" data-testid="recruitment-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              Recruitment
            </h1>
            <p className="text-slate-400 text-sm">Manage job openings and hiring</p>
          </div>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#c9a227] text-[#0b1c2d] hover:bg-[#c9a227]/90">
          <Plus className="w-4 h-4 mr-2" /> New Position
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="glass-card">
          <CardContent className="p-4">
            <Briefcase className="w-6 h-6 text-[#c9a227] mb-2" />
            <p className="text-2xl font-bold text-[#c9a227]">{requests.length}</p>
            <p className="text-xs text-slate-400">Total Positions</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Search className="w-6 h-6 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-green-400">{openPositions}</p>
            <p className="text-xs text-slate-400">Open Positions</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Users className="w-6 h-6 text-blue-400 mb-2" />
            <p className="text-2xl font-bold text-blue-400">{totalApplicants}</p>
            <p className="text-xs text-slate-400">Total Applicants</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Clock className="w-6 h-6 text-red-400 mb-2" />
            <p className="text-2xl font-bold text-red-400">{requests.filter(r => r.urgency === "urgent").length}</p>
            <p className="text-xs text-slate-400">Urgent Hires</p>
          </CardContent>
        </Card>
      </div>

      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 overflow-y-auto">
          <Card className="glass-card w-full max-w-lg my-8">
            <CardHeader>
              <CardTitle className="text-white">Create Recruitment Request</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400">Position Title</label>
                  <Input
                    value={formData.position_title}
                    onChange={(e) => setFormData({ ...formData, position_title: e.target.value })}
                    className="input-sovereign"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400">Department</label>
                    <select
                      className="w-full mt-1 bg-slate-900 border border-[#c9a227]/20 rounded-sm p-2 text-white"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      required
                    >
                      <option value="">Select Department</option>
                      <option value="Engineering">Engineering</option>
                      <option value="PMO">PMO</option>
                      <option value="Finance">Finance</option>
                      <option value="Human Resources">Human Resources</option>
                      <option value="Operations">Operations</option>
                      <option value="Executive">Executive</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Experience (years)</label>
                    <Input
                      type="number"
                      value={formData.experience_years}
                      onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                      className="input-sovereign"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Job Description</label>
                  <textarea
                    value={formData.job_description}
                    onChange={(e) => setFormData({ ...formData, job_description: e.target.value })}
                    className="w-full mt-1 bg-slate-900 border border-[#c9a227]/20 rounded-sm p-2 text-white h-20"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400">Required Skills (comma separated)</label>
                  <Input
                    value={formData.required_skills}
                    onChange={(e) => setFormData({ ...formData, required_skills: e.target.value })}
                    className="input-sovereign"
                    placeholder="e.g., PMP, Leadership, Excel"
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-slate-400">Min Salary ($)</label>
                    <Input
                      type="number"
                      value={formData.salary_range_min}
                      onChange={(e) => setFormData({ ...formData, salary_range_min: e.target.value })}
                      className="input-sovereign"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Max Salary ($)</label>
                    <Input
                      type="number"
                      value={formData.salary_range_max}
                      onChange={(e) => setFormData({ ...formData, salary_range_max: e.target.value })}
                      className="input-sovereign"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Urgency</label>
                    <select
                      className="w-full mt-1 bg-slate-900 border border-[#c9a227]/20 rounded-sm p-2 text-white"
                      value={formData.urgency}
                      onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-[#c9a227] text-[#0b1c2d]">Create</Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Positions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {requests.map((req) => (
          <Card key={req.id} className="glass-card hover:border-[#c9a227]/40 transition-all">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <CardTitle className="text-white text-lg">{req.position_title}</CardTitle>
                <Badge className={getUrgencyBadge(req.urgency)}>{req.urgency}</Badge>
              </div>
              <div className="flex gap-2">
                <Badge className="bg-slate-700/50 text-slate-300">{req.department}</Badge>
                <Badge className={getStatusBadge(req.status)}>{req.status}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-slate-400 text-sm line-clamp-2">{req.job_description}</p>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-500">Experience:</span>
                  <span className="text-white ml-1">{req.experience_years}+ years</span>
                </div>
                <div>
                  <span className="text-slate-500">Applicants:</span>
                  <span className="text-blue-400 ml-1">{req.applicants || 0}</span>
                </div>
              </div>

              <div>
                <span className="text-slate-500 text-sm">Salary Range:</span>
                <span className="text-[#c9a227] ml-1">
                  {formatCurrency(req.salary_range_min)} - {formatCurrency(req.salary_range_max)}
                </span>
              </div>

              <div className="flex flex-wrap gap-1">
                {req.required_skills?.slice(0, 3).map((skill, idx) => (
                  <Badge key={idx} className="bg-slate-700/50 text-slate-300 text-xs">{skill}</Badge>
                ))}
                {req.required_skills?.length > 3 && (
                  <Badge className="bg-slate-700/50 text-slate-300 text-xs">+{req.required_skills.length - 3}</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default RecruitmentPage;
